import { css } from "lit";

/** Shared styles (buttons, fields, cards, badges…) included in every component. */
export const shared = css`
  * { box-sizing: border-box; }
  h1, h2, h3, h4, p, ul { margin: 0; padding: 0; }
  ul { list-style: none; }
  a { color: inherit; text-decoration: none; }
  button { font-family: inherit; cursor: pointer; }
  svg { display: block; }
  :focus-visible { outline: 2px solid var(--blue-600); outline-offset: 2px; border-radius: 4px; }

  .container { width: 100%; max-width: var(--container); margin: 0 auto; padding: 0 32px; }
  @media (max-width: 640px) { .container { padding: 0 16px; } }

  /* Buttons */
  .btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    border: 1px solid transparent; border-radius: var(--r-sm);
    font-weight: 600; font-size: 14px; padding: 10px 18px; white-space: nowrap;
    transition: transform .15s var(--ease), box-shadow .18s var(--ease), background .18s var(--ease), border-color .18s var(--ease);
  }
  .btn svg { width: 16px; height: 16px; flex-shrink: 0; }
  .btn-primary { background: var(--blue-600); color: var(--white); box-shadow: var(--shadow-sm); }
  .btn-primary:hover { background: #2760d6; box-shadow: var(--shadow-md); transform: translateY(-1px); }
  .btn-secondary { background: var(--white); color: var(--ink); border-color: var(--slate-200); }
  .btn-secondary:hover { border-color: var(--blue-500); color: var(--blue-600); background: var(--blue-50); }
  .btn-ghost { background: transparent; color: var(--slate-600); }
  .btn-ghost:hover { background: var(--slate-100); color: var(--ink); }
  .btn-sm { padding: 7px 12px; font-size: 13px; }
  .btn-block { width: 100%; }
  .btn:disabled, .btn[aria-disabled="true"] { opacity: .5; cursor: not-allowed; transform: none; box-shadow: none; }

  .icon-btn {
    width: 32px; height: 32px; border-radius: var(--r-sm); border: 1px solid var(--slate-200);
    background: var(--white); display: inline-flex; align-items: center; justify-content: center;
    color: var(--slate-600); transition: all .15s var(--ease);
  }
  .icon-btn svg { width: 15px; height: 15px; }
  .icon-btn:hover { border-color: var(--blue-500); color: var(--blue-600); background: var(--blue-50); }

  /* Fields */
  .field { display: flex; flex-direction: column; gap: 6px; text-align: left; }
  .field label, .label { font-size: 12.5px; font-weight: 600; color: var(--slate-600); letter-spacing: .01em; }
  .input, .select, textarea.input {
    border: 1px solid var(--slate-200); background: var(--white); border-radius: var(--r-sm);
    padding: 11px 14px; font-size: 14.5px; font-family: inherit; color: var(--ink); width: 100%;
    transition: border-color .18s var(--ease), box-shadow .18s var(--ease);
  }
  .input::placeholder { color: var(--slate-400); }
  .input:focus, .select:focus { outline: none; border-color: var(--blue-500); box-shadow: var(--shadow-glow-blue); }
  .input[aria-invalid="true"] { border-color: var(--red); }
  textarea.input { min-height: 72px; resize: vertical; font-size: 13.5px; }

  /* Cards */
  .card { background: var(--white); border: 1px solid var(--slate-200); border-radius: var(--r-lg); box-shadow: var(--shadow-xs); }
  .card-padded { padding: 28px; }
  @media (max-width: 640px) { .card-padded { padding: 20px; } }

  /* Badges */
  .badge { display: inline-flex; align-items: center; gap: 6px; padding: 4px 11px; border-radius: var(--r-pill); font-size: 12px; font-weight: 600; border: 1px solid transparent; white-space: nowrap; }
  .badge-green { background: var(--green-bg); color: var(--green); border-color: var(--green-line); }
  .badge-amber { background: var(--amber-bg); color: var(--amber); border-color: var(--amber-line); }
  .badge-red { background: var(--red-bg); color: var(--red); border-color: var(--red-line); }
  .badge-neutral { background: var(--neutral-bg); color: var(--slate-600); border-color: var(--neutral-line); }
  .badge-blue { background: var(--blue-100); color: var(--blue-600); border-color: #cfe0fd; }

  /* Page headers */
  .page-header { padding: 44px 0 8px; }
  .page-eyebrow { font-size: 12.5px; font-weight: 600; color: var(--blue-600); letter-spacing: .02em; text-transform: uppercase; margin-bottom: 10px; }
  .page-title { font-size: clamp(26px, 3.6vw, 34px); font-weight: 800; letter-spacing: -.02em; color: var(--navy-950); }
  .page-sub { color: var(--slate-600); font-size: 14.5px; margin-top: 8px; max-width: 620px; }

  /* Alerts */
  .alert { border-radius: var(--r-md); padding: 14px 16px; font-size: 13.5px; display: flex; gap: 12px; align-items: flex-start; }
  .alert svg { width: 18px; height: 18px; flex-shrink: 0; margin-top: 1px; }
  .alert-error { background: var(--red-bg); color: #9c2b26; border: 1px solid var(--red-line); }
  .alert-info { background: var(--blue-50); color: var(--navy-700); border: 1px solid var(--blue-100); }

  .mono { font-family: var(--font-mono); }
  .muted { color: var(--slate-400); }
  .spinner { width: 18px; height: 18px; border: 2px solid currentColor; border-right-color: transparent; border-radius: 50%; animation: spin .7s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
  .fade-up { animation: fadeUp .6s var(--ease) both; }
  @keyframes fadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
  .sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
`;
